"use client";

import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Loader2, CheckCircle2, AlertCircle, RotateCcw, FileText, Shield, ClipboardList, AlertTriangle, DollarSign, Users, Trash2, BookOpen } from "lucide-react";
import { Badge } from "../ui/badge";
import { getAssertionAbbreviation } from "../../utils/assertion-helpers";

type RbinDoc = {
  name: string;
  url: string;
  reference?: string;
  area?: string;
};

type Deficiency = {
  id: string;
  control_relevance: string;
  control_id: string[];
  deficiency_type: string;
  description: string | null;
  root_cause: string | null;
};

type Misstatement = {
  id: string;
  workspace: string;
  type: "Factual" | "Judgemental" | "Projected";
  data: any;
};

type ControlOwner = {
  id: string;
  name: string;
  designation: string;
  emailid: string;
  authorityCompetence: string;
  data: any;
};

type SubstantiveProcedure = {
  workspace: string;
  procedureName: string;
  data: any;
};

type Romm = {
  id: string;
  risk: string;
  assertion: string;
  workspace: string;
  controlIds: string[];
  data: any;
};

type InformationEntry = {
  information_name: string;
  workspace?: string[];
  workspace_prefix?: string[];
  file_name?: string;
  data: any;
};

type SelectedCategory = "documents" | "deficiencies" | "romms" | "controls" | "procedures" | "misstatements" | "control-owners" | "information" | null;

export default function RecycleBinPage() {
  const [docs, setDocs] = useState<RbinDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [status, setStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>("documents");
  const [deficiencies, setDeficiencies] = useState<Deficiency[]>([]);
  const [deficienciesLoading, setDeficienciesLoading] = useState<boolean>(false);
  const [restoringDeficiency, setRestoringDeficiency] = useState<string | null>(null);
  const [misstatements, setMisstatements] = useState<Misstatement[]>([]);
  const [misstatementsLoading, setMisstatementsLoading] = useState<boolean>(false);
  const [restoringMisstatement, setRestoringMisstatement] = useState<string | null>(null);
  const [controlOwners, setControlOwners] = useState<ControlOwner[]>([]);
  const [controlOwnersLoading, setControlOwnersLoading] = useState<boolean>(false);
  const [restoringControlOwner, setRestoringControlOwner] = useState<string | null>(null);
  const [informationEntries, setInformationEntries] = useState<InformationEntry[]>([]);
  const [informationLoading, setInformationLoading] = useState<boolean>(false);
  const [restoringInformation, setRestoringInformation] = useState<string | null>(null);
  const [substantiveProcedures, setSubstantiveProcedures] = useState<SubstantiveProcedure[]>([]);
  const [substantiveProceduresLoading, setSubstantiveProceduresLoading] = useState<boolean>(false);
  const [restoringProcedure, setRestoringProcedure] = useState<{ workspace: string; procedureName: string } | null>(null);
  const [romms, setRomms] = useState<Romm[]>([]);
  const [rommsLoading, setRommsLoading] = useState<boolean>(false);
  const [restoringRomm, setRestoringRomm] = useState<string | null>(null);
  const [controls, setControls] = useState<any[]>([]);
  const [controlsLoading, setControlsLoading] = useState<boolean>(false);
  const [restoringControl, setRestoringControl] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [deletingPermanently, setDeletingPermanently] = useState<boolean>(false);
  const [selectedDeficiencyIds, setSelectedDeficiencyIds] = useState<Set<string>>(new Set());
  const [selectedMisstatementIds, setSelectedMisstatementIds] = useState<Set<string>>(new Set());
  const [selectedControlOwnerIds, setSelectedControlOwnerIds] = useState<Set<string>>(new Set());
  const [selectedInformationNames, setSelectedInformationNames] = useState<Set<string>>(new Set());
  const [selectedProcedureKeys, setSelectedProcedureKeys] = useState<Set<string>>(new Set()); // "workspace\0procedureName"
  const [selectedRommIds, setSelectedRommIds] = useState<Set<string>>(new Set());
  const [selectedControlIds, setSelectedControlIds] = useState<Set<string>>(new Set());
  const [deletingPermanentlyLibrary, setDeletingPermanentlyLibrary] = useState<boolean>(false);

  const loadDocs = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.loadRbinDocuments?.();
      if (res?.success) {
        setDocs(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.error || "Failed to load recycle bin documents");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error while loading recycle bin documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await loadDocs();
    };
    run();
    return () => {
      mounted = false;
    };
  }, []);

  const loadDeficiencies = async () => {
    try {
      setDeficienciesLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.downloadRbinJson?.("Libraries_Deficiency.json");
      if (res?.success && res?.data) {
        const raw = res.data.libraries_deficiency || {};
        const items = Object.entries(raw).map(([id, value]: [string, any]) => ({
          id,
          control_relevance: value.control_relevance || "",
          control_id: Array.isArray(value.control_id) ? value.control_id : [],
          deficiency_type: value.deficiency_type || "",
          description: value.description ?? null,
          root_cause: value.root_cause ?? null,
        }));
        setDeficiencies(items);
      } else {
        setDeficiencies([]);
      }
    } catch (e: any) {
      console.error("Failed to load deficiencies from rbin", e);
      setError(e?.message || "Failed to load deficiencies");
      setDeficiencies([]);
    } finally {
      setDeficienciesLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCategory === "deficiencies") {
      loadDeficiencies();
    } else if (selectedCategory === "misstatements") {
      loadMisstatements();
    } else if (selectedCategory === "control-owners") {
      loadControlOwners();
    } else if (selectedCategory === "information") {
      loadInformation();
    } else if (selectedCategory === "procedures") {
      loadSubstantiveProcedures();
    } else if (selectedCategory === "romms") {
      loadRomms();
    } else if (selectedCategory === "controls") {
      loadControls();
    }
  }, [selectedCategory]);

  const loadMisstatements = async () => {
    try {
      setMisstatementsLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.downloadRbinJson?.("Libraries_Misstatement.json");
      if (res?.success && res?.data) {
        const raw = res.data.Libraries_Misstatement?.misstatements || {};
        const items = Object.entries(raw).map(([id, value]: [string, any]) => ({
          id,
          workspace: value.workspace || "",
          type: value.type || "Factual",
          data: value,
        }));
        setMisstatements(items);
      } else {
        setMisstatements([]);
      }
    } catch (e: any) {
      console.error("Failed to load misstatements from rbin", e);
      setError(e?.message || "Failed to load misstatements");
      setMisstatements([]);
    } finally {
      setMisstatementsLoading(false);
    }
  };

  const handleRestoreMisstatement = async (misstatementId: string) => {
    try {
      setRestoringMisstatement(misstatementId);
      setError("");
      setStatus(null);
      
      const res = await (window as any)?.sharePointAPI?.restoreMisstatement?.(misstatementId);
      
      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored misstatement ${misstatementId}`
        });
        // Reload the misstatements list after successful restore
        await loadMisstatements();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore misstatement ${misstatementId}`
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring misstatement ${misstatementId}`
      });
    } finally {
      setRestoringMisstatement(null);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const loadControlOwners = async () => {
    try {
      setControlOwnersLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.downloadRbinJson?.("Libraries_ControlOwners.json");
      if (res?.success && res?.data) {
        const raw = res.data.controlOwners || {};
        const items = Object.entries(raw).map(([id, value]: [string, any]) => ({
          id,
          name: value.name || "",
          designation: value.designation || "",
          emailid: value.emailid || "",
          authorityCompetence: value.authorityCompetence || "",
          data: value,
        }));
        setControlOwners(items);
      } else {
        setControlOwners([]);
      }
    } catch (e: any) {
      console.error("Failed to load control owners from rbin", e);
      setError(e?.message || "Failed to load control owners");
      setControlOwners([]);
    } finally {
      setControlOwnersLoading(false);
    }
  };

  const handleRestoreControlOwner = async (ownerId: string) => {
    try {
      setRestoringControlOwner(ownerId);
      setError("");
      setStatus(null);
      
      const res = await (window as any)?.sharePointAPI?.restoreControlOwner?.(ownerId);
      
      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored control owner ${ownerId}`
        });
        // Reload the control owners list after successful restore
        await loadControlOwners();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore control owner ${ownerId}`
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring control owner ${ownerId}`
      });
    } finally {
      setRestoringControlOwner(null);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const loadInformation = async () => {
    try {
      setInformationLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.downloadRbinJson?.("Libraries_Information.json");
      if (res?.success && res?.data) {
        const raw = Array.isArray(res.data.information) ? res.data.information : [];
        const items: InformationEntry[] = raw.map((item: any) => ({
          information_name: item.information_name || "",
          workspace: Array.isArray(item.workspace) ? item.workspace : [],
          workspace_prefix: Array.isArray(item.workspace_prefix) ? item.workspace_prefix : [],
          file_name: item.file_name,
          data: item,
        }));
        setInformationEntries(items);
      } else {
        setInformationEntries([]);
      }
    } catch (e: any) {
      console.error("Failed to load information from rbin", e);
      setError(e?.message || "Failed to load information");
      setInformationEntries([]);
    } finally {
      setInformationLoading(false);
    }
  };

  const handleRestoreInformation = async (entryName: string) => {
    try {
      setRestoringInformation(entryName);
      setError("");
      setStatus(null);

      const res = await (window as any)?.sharePointAPI?.restoreInformationEntry?.(entryName);

      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored information entry "${entryName}"`,
        });
        await loadInformation();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore information entry "${entryName}"`,
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring information entry "${entryName}"`,
      });
    } finally {
      setRestoringInformation(null);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const loadSubstantiveProcedures = async () => {
    try {
      setSubstantiveProceduresLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.downloadRbinJson?.("SubstantiveProcedures.json");
      if (res?.success && res?.data) {
        const raw = res.data || {};
        const items: SubstantiveProcedure[] = [];
        
        // Iterate through all workspaces
        Object.entries(raw).forEach(([workspace, procedures]: [string, any]) => {
          if (procedures && typeof procedures === 'object') {
            // Iterate through all procedures in this workspace
            Object.entries(procedures).forEach(([procedureName, procedureData]: [string, any]) => {
              items.push({
                workspace,
                procedureName,
                data: procedureData,
              });
            });
          }
        });
        
        setSubstantiveProcedures(items);
      } else {
        setSubstantiveProcedures([]);
      }
    } catch (e: any) {
      console.error("Failed to load substantive procedures from rbin", e);
      setError(e?.message || "Failed to load substantive procedures");
      setSubstantiveProcedures([]);
    } finally {
      setSubstantiveProceduresLoading(false);
    }
  };

  const handleRestoreSubstantiveProcedure = async (workspace: string, procedureName: string) => {
    try {
      setRestoringProcedure({ workspace, procedureName });
      setError("");
      setStatus(null);
      
      const res = await (window as any)?.sharePointAPI?.restoreSubstantiveProcedure?.({
        workspace,
        procedureName,
      });
      
      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored procedure ${procedureName} to ${workspace}`
        });
        // Reload the procedures list after successful restore
        await loadSubstantiveProcedures();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore procedure ${procedureName}`
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring procedure ${procedureName}`
      });
    } finally {
      setRestoringProcedure(null);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const loadRomms = async () => {
    try {
      setRommsLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.downloadRbinJson?.("Libraries_Romm.json");
      if (res?.success && res?.data) {
        const raw = res.data.romm_library || [];
        const items = raw.map((romm: any) => ({
          id: romm["romm-id"] || "",
          risk: romm.risk || "",
          assertion: typeof romm.assertion === 'string' ? romm.assertion : (romm.assertion ? String(romm.assertion) : ""),
          workspace: romm.workspace || "",
          controlIds: Array.isArray(romm.control_id) 
            ? romm.control_id 
            : romm.control_id 
            ? [romm.control_id] 
            : [],
          data: romm,
        }));
        setRomms(items);
      } else {
        setRomms([]);
      }
    } catch (e: any) {
      console.error("Failed to load RoMMs from rbin", e);
      setError(e?.message || "Failed to load RoMMs");
      setRomms([]);
    } finally {
      setRommsLoading(false);
    }
  };

  const handleRestoreRomm = async (rommId: string) => {
    try {
      setRestoringRomm(rommId);
      setError("");
      setStatus(null);
      
      const res = await (window as any)?.sharePointAPI?.restoreRomm?.(rommId);
      
      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored RoMM ${rommId}`
        });
        // Reload the RoMMs list after successful restore
        await loadRomms();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore RoMM ${rommId}`
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring RoMM ${rommId}`
      });
    } finally {
      setRestoringRomm(null);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const loadControls = async () => {
    try {
      setControlsLoading(true);
      setError("");
      const res = await (window as any)?.sharePointAPI?.downloadRbinJson?.("Libraries_RCM.json");
      if (res?.success && res?.data) {
        const raw = res.data.libraries_RCM || {};
        const items = Object.entries(raw).map(([controlId, value]: [string, any]) => ({
          controlId,
          controlName: value.control_name || controlId,
          typeOfControl: value.TypeOfControl || "Unknown",
          nature: value.Nature || "Unknown",
          workspace: Array.isArray(value.workspace) ? value.workspace : (value.workspace ? [value.workspace] : []),
          data: value,
        }));
        setControls(items);
      } else {
        setControls([]);
      }
    } catch (e: any) {
      console.error("Failed to load controls from rbin", e);
      setError(e?.message || "Failed to load controls");
      setControls([]);
    } finally {
      setControlsLoading(false);
    }
  };

  const handleRestoreControl = async (controlId: string) => {
    try {
      setRestoringControl(controlId);
      setError("");
      setStatus(null);
      
      const res = await (window as any)?.sharePointAPI?.restoreControl?.(controlId);
      
      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored control ${controlId}`
        });
        // Reload the controls list after successful restore
        await loadControls();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore control ${controlId}`
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring control ${controlId}`
      });
    } finally {
      setRestoringControl(null);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const handleRestoreDeficiency = async (deficiencyId: string) => {
    try {
      setRestoringDeficiency(deficiencyId);
      setError("");
      setStatus(null);
      
      const res = await (window as any)?.sharePointAPI?.restoreDeficiency?.(deficiencyId);
      
      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored deficiency ${deficiencyId}`
        });
        // Reload the deficiencies list after successful restore
        await loadDeficiencies();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore deficiency ${deficiencyId}`
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring deficiency ${deficiencyId}`
      });
    } finally {
      setRestoringDeficiency(null);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const handleRestore = async (fileName: string) => {
    try {
      setLoading(true);
      setError("");
      setStatus(null);
      
      const res = await (window as any)?.sharePointAPI?.restoreRbinFile?.(fileName);
      
      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully restored ${fileName}`
        });
        // Reload the documents list after successful restore
        await loadDocs();
      } else {
        setStatus({
          success: false,
          message: res?.error || `Failed to restore ${fileName}`
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || `Unexpected error while restoring ${fileName}`
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const handleFileClick = (url: string) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleToggleFileSelection = (fileName: string) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      } else {
        newSet.add(fileName);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === filteredDocs.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredDocs.map((d) => d.name)));
    }
  };

  const handleDeletePermanently = async () => {
    if (selectedFiles.size === 0) {
      return;
    }

    const fileNames = Array.from(selectedFiles);
    const confirmMessage = `Are you sure you want to permanently delete ${fileNames.length} file(s)? This action cannot be undone.\n\nFiles:\n${fileNames.join("\n")}`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingPermanently(true);
      setError("");
      setStatus(null);

      const res = await (window as any)?.sharePointAPI?.deletePermanentlyRbinFiles?.(fileNames);

      if (res?.success) {
        setStatus({
          success: true,
          message: res.message || `Successfully deleted ${res.data?.deleted_files?.length || 0} file(s) permanently`
        });
        // Clear selection and reload the documents list
        setSelectedFiles(new Set());
        await loadDocs();
      } else {
        setStatus({
          success: false,
          message: res?.error || "Failed to delete files permanently"
        });
      }
    } catch (e: any) {
      setStatus({
        success: false,
        message: e?.message || "Unexpected error while deleting files permanently"
      });
    } finally {
      setDeletingPermanently(false);
      setTimeout(() => {
        setStatus(null);
      }, 5000);
    }
  };

  const runPermanentDeleteLibrary = async (
    ids: string[] | { workspace: string; procedureName: string }[],
    api: () => Promise<{ success?: boolean; error?: string; message?: string }>,
    clearSelection: () => void,
    reload: () => Promise<void>,
    label: string
  ) => {
    if (!ids.length) return;
    if (!confirm(`Permanently delete ${ids.length} ${label}? This cannot be undone.`)) return;
    try {
      setDeletingPermanentlyLibrary(true);
      setError("");
      setStatus(null);
      const res = await api();
      if (res?.success) {
        setStatus({ success: true, message: res.message || `Permanently deleted ${ids.length} ${label}.` });
        clearSelection();
        await reload();
      } else {
        setStatus({ success: false, message: res?.error || "Failed to delete permanently." });
      }
    } catch (e: any) {
      setStatus({ success: false, message: e?.message || "Error during permanent delete." });
    } finally {
      setDeletingPermanentlyLibrary(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const handlePermanentDeleteDeficiencies = () =>
    runPermanentDeleteLibrary(
      Array.from(selectedDeficiencyIds),
      () => (window as any).sharePointAPI?.deletePermanentlyRbinDeficiencies?.(Array.from(selectedDeficiencyIds)),
      () => setSelectedDeficiencyIds(new Set()),
      loadDeficiencies,
      "deficiency(ies)"
    );
  const handlePermanentDeleteMisstatements = () =>
    runPermanentDeleteLibrary(
      Array.from(selectedMisstatementIds),
      () => (window as any).sharePointAPI?.deletePermanentlyRbinMisstatements?.(Array.from(selectedMisstatementIds)),
      () => setSelectedMisstatementIds(new Set()),
      loadMisstatements,
      "misstatement(s)"
    );
  const handlePermanentDeleteControlOwners = () =>
    runPermanentDeleteLibrary(
      Array.from(selectedControlOwnerIds),
      () => (window as any).sharePointAPI?.deletePermanentlyRbinControlOwners?.(Array.from(selectedControlOwnerIds)),
      () => setSelectedControlOwnerIds(new Set()),
      loadControlOwners,
      "control owner(s)"
    );
  const handlePermanentDeleteInformation = () =>
    runPermanentDeleteLibrary(
      Array.from(selectedInformationNames),
      () => (window as any).sharePointAPI?.deletePermanentlyRbinInformation?.(Array.from(selectedInformationNames)),
      () => setSelectedInformationNames(new Set()),
      loadInformation,
      "information entry(ies)"
    );
  const handlePermanentDeleteProcedures = () => {
    const items = Array.from(selectedProcedureKeys).map((k) => {
      const [workspace, procedureName] = k.split("\0");
      return { workspace, procedureName };
    });
    runPermanentDeleteLibrary(
      items,
      () => (window as any).sharePointAPI?.deletePermanentlyRbinProcedures?.(items),
      () => setSelectedProcedureKeys(new Set()),
      loadSubstantiveProcedures,
      "procedure(s)"
    );
  };
  const handlePermanentDeleteRomms = () =>
    runPermanentDeleteLibrary(
      Array.from(selectedRommIds),
      () => (window as any).sharePointAPI?.deletePermanentlyRbinRomms?.(Array.from(selectedRommIds)),
      () => setSelectedRommIds(new Set()),
      loadRomms,
      "RoMM(s)"
    );
  const handlePermanentDeleteControls = () =>
    runPermanentDeleteLibrary(
      Array.from(selectedControlIds),
      () => (window as any).sharePointAPI?.deletePermanentlyRbinControls?.(Array.from(selectedControlIds)),
      () => setSelectedControlIds(new Set()),
      loadControls,
      "control(s)"
    );

  // Filter out JSON files from the documents list
  const filteredDocs = docs.filter((doc) => {
    const fileName = doc.name.toLowerCase();
    return !fileName.endsWith('.json');
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Recycle Bin</h2>
          <p className="text-sm text-white/60">
            View and restore deleted documents
          </p>
        </div>
      </div>

      {/* Category Panels */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card 
          className={`border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer ${
            selectedCategory === "romms" ? "border-blue-500/50 bg-blue-500/10" : ""
          }`}
          onClick={() => setSelectedCategory("romms")}
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4 text-blue-400" />
              RoMMs
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-white/60">View deleted RoMMs</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer ${
            selectedCategory === "controls" ? "border-green-500/50 bg-green-500/10" : ""
          }`}
          onClick={() => setSelectedCategory("controls")}
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Shield className="h-4 w-4 text-green-400" />
              Internal Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-white/60">View deleted internal controls</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer ${
            selectedCategory === "procedures" ? "border-purple-500/50 bg-purple-500/10" : ""
          }`}
          onClick={() => setSelectedCategory("procedures")}
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <ClipboardList className="h-4 w-4 text-purple-400" />
              Substantive Procedures
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-white/60">View deleted substantive procedures</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer ${
            selectedCategory === "deficiencies" ? "border-red-500/50 bg-red-500/10" : ""
          }`}
          onClick={() => setSelectedCategory("deficiencies")}
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              Deficiencies
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-white/60">View deleted deficiencies</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer ${
            selectedCategory === "misstatements" ? "border-yellow-500/50 bg-yellow-500/10" : ""
          }`}
          onClick={() => setSelectedCategory("misstatements")}
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <DollarSign className="h-4 w-4 text-yellow-400" />
              Misstatements
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-white/60">View deleted misstatements</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer ${
            selectedCategory === "control-owners" ? "border-purple-500/50 bg-purple-500/10" : ""
          }`}
          onClick={() => setSelectedCategory("control-owners")}
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4 text-purple-400" />
              Control Owners
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-white/60">View deleted control owners</p>
          </CardContent>
        </Card>

        <Card 
          className={`border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10 cursor-pointer ${
            selectedCategory === "information" ? "border-amber-500/50 bg-amber-500/10" : ""
          }`}
          onClick={() => setSelectedCategory("information")}
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <BookOpen className="h-4 w-4 text-amber-400" />
              Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-xs text-white/60">View deleted information entries</p>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {selectedCategory === "deficiencies" 
              ? "Deleted Deficiencies" 
              : selectedCategory === "misstatements"
              ? "Deleted Misstatements"
              : selectedCategory === "control-owners"
              ? "Deleted Control Owners"
              : selectedCategory === "information"
              ? "Deleted Information"
              : selectedCategory === "procedures"
              ? "Deleted Substantive Procedures"
              : selectedCategory === "romms"
              ? "Deleted RoMMs"
              : "Deleted Documents"}
          </h3>
          {selectedCategory !== "documents" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedCategory("documents")}
            >
              Back to Documents
            </Button>
          )}
        </div>
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-4">
            {status && (
              <div className={`mb-4 p-3 rounded-md border ${
                status.success 
                  ? "border-green-500/20 bg-green-500/10 text-green-300" 
                  : "border-red-500/20 bg-red-500/10 text-red-300"
              }`}>
                <div className="flex items-center gap-2">
                  {status.success ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <span className="text-sm">{status.message}</span>
                </div>
              </div>
            )}
            
            {selectedCategory === "deficiencies" ? (
              <>
                {deficienciesLoading && <div className="text-sm text-white/60">Loading deficiencies...</div>}
                {!deficienciesLoading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!deficienciesLoading && !error && deficiencies.length === 0 && (
                  <div className="text-sm text-white/60">No deficiencies in recycle bin.</div>
                )}
                {!deficienciesLoading && !error && deficiencies.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedDeficiencyIds.size === deficiencies.length}
                          onChange={() => {
                            if (selectedDeficiencyIds.size === deficiencies.length) {
                              setSelectedDeficiencyIds(new Set());
                            } else {
                              setSelectedDeficiencyIds(new Set(deficiencies.map((d) => d.id)));
                            }
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500"
                        />
                        <span className="text-sm text-white/60">
                          {selectedDeficiencyIds.size > 0 ? `${selectedDeficiencyIds.size} selected` : "Select all"}
                        </span>
                      </div>
                      {selectedDeficiencyIds.size > 0 && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handlePermanentDeleteDeficiencies}
                          disabled={deletingPermanentlyLibrary}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          {deletingPermanentlyLibrary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Permanently ({selectedDeficiencyIds.size})
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                    {deficiencies.map((def) => {
                      const isSelected = selectedDeficiencyIds.has(def.id);
                      return (
                      <li
                        key={def.id}
                        className={`flex items-center gap-3 rounded-md border p-3 transition-colors cursor-pointer ${isSelected ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`}
                        onClick={() => {
                          setSelectedDeficiencyIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(def.id)) next.delete(def.id);
                            else next.add(def.id);
                            return next;
                          });
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedDeficiencyIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(def.id)) next.delete(def.id);
                              else next.add(def.id);
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {def.id}
                            </Badge>
                            <span className="text-sm font-medium text-white">
                              {def.deficiency_type || "Deficiency"}
                            </span>
                          </div>
                          {def.control_id && def.control_id.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {def.control_id.map((cid) => (
                                <Badge key={cid} variant="outline" className="text-[10px]">
                                  {cid}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {def.description && (
                            <div className="mt-2 text-xs text-white/70 line-clamp-2">
                              <span dangerouslySetInnerHTML={{ __html: def.description }} />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleRestoreDeficiency(def.id); }}
                            disabled={restoringDeficiency === def.id}
                            className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            title="Restore"
                          >
                            {restoringDeficiency === def.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </li>
                    );
                    })}
                  </ul>
                  </>
                )}
              </>
            ) : selectedCategory === "misstatements" ? (
              <>
                {misstatementsLoading && <div className="text-sm text-white/60">Loading misstatements...</div>}
                {!misstatementsLoading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!misstatementsLoading && !error && misstatements.length === 0 && (
                  <div className="text-sm text-white/60">No misstatements in recycle bin.</div>
                )}
                {!misstatementsLoading && !error && misstatements.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedMisstatementIds.size === misstatements.length}
                          onChange={() => {
                            if (selectedMisstatementIds.size === misstatements.length) setSelectedMisstatementIds(new Set());
                            else setSelectedMisstatementIds(new Set(misstatements.map((m) => m.id)));
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500"
                        />
                        <span className="text-sm text-white/60">{selectedMisstatementIds.size > 0 ? `${selectedMisstatementIds.size} selected` : "Select all"}</span>
                      </div>
                      {selectedMisstatementIds.size > 0 && (
                        <Button size="sm" variant="destructive" onClick={handlePermanentDeleteMisstatements} disabled={deletingPermanentlyLibrary} className="bg-red-600 hover:bg-red-700 text-white">
                          {deletingPermanentlyLibrary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Permanently ({selectedMisstatementIds.size})
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                    {misstatements.map((mis) => {
                      const isSelected = selectedMisstatementIds.has(mis.id);
                      return (
                      <li
                        key={mis.id}
                        className={`flex items-center gap-3 rounded-md border p-3 transition-colors cursor-pointer ${isSelected ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`}
                        onClick={() => {
                          setSelectedMisstatementIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(mis.id)) next.delete(mis.id);
                            else next.add(mis.id);
                            return next;
                          });
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => { e.stopPropagation(); setSelectedMisstatementIds((prev) => { const n = new Set(prev); if (n.has(mis.id)) n.delete(mis.id); else n.add(mis.id); return n; }); }}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {mis.id}
                            </Badge>
                            <span className="text-sm font-medium text-white">
                              {mis.type} Misstatement
                            </span>
                          </div>
                          {mis.workspace && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                Workspace: {mis.workspace}
                              </Badge>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleRestoreMisstatement(mis.id); }}
                            disabled={restoringMisstatement === mis.id}
                            className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            title="Restore"
                          >
                            {restoringMisstatement === mis.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </li>
                    );
                    })}
                  </ul>
                  </>
                )}
              </>
            ) : selectedCategory === "control-owners" ? (
              <>
                {controlOwnersLoading && <div className="text-sm text-white/60">Loading control owners...</div>}
                {!controlOwnersLoading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!controlOwnersLoading && !error && controlOwners.length === 0 && (
                  <div className="text-sm text-white/60">No control owners in recycle bin.</div>
                )}
                {!controlOwnersLoading && !error && controlOwners.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={selectedControlOwnerIds.size === controlOwners.length} onChange={() => { if (selectedControlOwnerIds.size === controlOwners.length) setSelectedControlOwnerIds(new Set()); else setSelectedControlOwnerIds(new Set(controlOwners.map((o) => o.id))); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500" />
                        <span className="text-sm text-white/60">{selectedControlOwnerIds.size > 0 ? `${selectedControlOwnerIds.size} selected` : "Select all"}</span>
                      </div>
                      {selectedControlOwnerIds.size > 0 && (
                        <Button size="sm" variant="destructive" onClick={handlePermanentDeleteControlOwners} disabled={deletingPermanentlyLibrary} className="bg-red-600 hover:bg-red-700 text-white">
                          {deletingPermanentlyLibrary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Permanently ({selectedControlOwnerIds.size})
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                    {controlOwners.map((owner) => {
                      const isSelected = selectedControlOwnerIds.has(owner.id);
                      return (
                      <li
                        key={owner.id}
                        className={`flex items-center gap-3 rounded-md border p-3 transition-colors cursor-pointer ${isSelected ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`}
                        onClick={() => { setSelectedControlOwnerIds((prev) => { const n = new Set(prev); if (n.has(owner.id)) n.delete(owner.id); else n.add(owner.id); return n; }); }}
                      >
                        <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); setSelectedControlOwnerIds((prev) => { const n = new Set(prev); if (n.has(owner.id)) n.delete(owner.id); else n.add(owner.id); return n; }); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {owner.id}
                            </Badge>
                            <span className="text-sm font-medium text-white">
                              {owner.name || "Unnamed Owner"}
                            </span>
                          </div>
                          {owner.designation && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                {owner.designation}
                              </Badge>
                            </div>
                          )}
                          {owner.emailid && (
                            <div className="mt-1 text-xs text-white/70">
                              {owner.emailid}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleRestoreControlOwner(owner.id); }}
                            disabled={restoringControlOwner === owner.id}
                            className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            title="Restore"
                          >
                            {restoringControlOwner === owner.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </li>
                    );
                    })}
                  </ul>
                  </>
                )}
              </>
            ) : selectedCategory === "information" ? (
              <>
                {informationLoading && <div className="text-sm text-white/60">Loading information...</div>}
                {!informationLoading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!informationLoading && !error && informationEntries.length === 0 && (
                  <div className="text-sm text-white/60">No information entries in recycle bin.</div>
                )}
                {!informationLoading && !error && informationEntries.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={selectedInformationNames.size === informationEntries.length} onChange={() => { if (selectedInformationNames.size === informationEntries.length) setSelectedInformationNames(new Set()); else setSelectedInformationNames(new Set(informationEntries.map((e) => e.information_name))); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500" />
                        <span className="text-sm text-white/60">{selectedInformationNames.size > 0 ? `${selectedInformationNames.size} selected` : "Select all"}</span>
                      </div>
                      {selectedInformationNames.size > 0 && (
                        <Button size="sm" variant="destructive" onClick={handlePermanentDeleteInformation} disabled={deletingPermanentlyLibrary} className="bg-red-600 hover:bg-red-700 text-white">
                          {deletingPermanentlyLibrary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Permanently ({selectedInformationNames.size})
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                    {informationEntries.map((entry) => {
                      const isSelected = selectedInformationNames.has(entry.information_name);
                      return (
                      <li
                        key={entry.information_name}
                        className={`flex items-center gap-3 rounded-md border p-3 transition-colors cursor-pointer ${isSelected ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`}
                        onClick={() => { setSelectedInformationNames((prev) => { const n = new Set(prev); const k = entry.information_name; if (n.has(k)) n.delete(k); else n.add(k); return n; }); }}
                      >
                        <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); setSelectedInformationNames((prev) => { const n = new Set(prev); const k = entry.information_name; if (n.has(k)) n.delete(k); else n.add(k); return n; }); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-white">
                              {entry.information_name || "Unnamed"}
                            </span>
                          </div>
                          {entry.workspace_prefix && entry.workspace_prefix.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {entry.workspace_prefix.slice(0, 5).map((prefix: string, idx: number) => (
                                <Badge key={idx} variant="outline" className="text-[10px]">
                                  {prefix}
                                </Badge>
                              ))}
                              {entry.workspace_prefix.length > 5 && (
                                <Badge variant="outline" className="text-[10px]">+{entry.workspace_prefix.length - 5}</Badge>
                              )}
                            </div>
                          )}
                          {entry.file_name && (
                            <div className="mt-1 text-xs text-white/60">
                              {entry.file_name}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleRestoreInformation(entry.information_name); }}
                            disabled={restoringInformation === entry.information_name}
                            className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            title="Restore"
                          >
                            {restoringInformation === entry.information_name ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </li>
                    );
                    })}
                  </ul>
                  </>
                )}
              </>
            ) : selectedCategory === "procedures" ? (
              <>
                {substantiveProceduresLoading && <div className="text-sm text-white/60">Loading substantive procedures...</div>}
                {!substantiveProceduresLoading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!substantiveProceduresLoading && !error && substantiveProcedures.length === 0 && (
                  <div className="text-sm text-white/60">No substantive procedures in recycle bin.</div>
                )}
                {!substantiveProceduresLoading && !error && substantiveProcedures.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={selectedProcedureKeys.size === substantiveProcedures.length} onChange={() => { if (selectedProcedureKeys.size === substantiveProcedures.length) setSelectedProcedureKeys(new Set()); else setSelectedProcedureKeys(new Set(substantiveProcedures.map((p) => `${p.workspace}\0${p.procedureName}`))); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500" />
                        <span className="text-sm text-white/60">{selectedProcedureKeys.size > 0 ? `${selectedProcedureKeys.size} selected` : "Select all"}</span>
                      </div>
                      {selectedProcedureKeys.size > 0 && (
                        <Button size="sm" variant="destructive" onClick={handlePermanentDeleteProcedures} disabled={deletingPermanentlyLibrary} className="bg-red-600 hover:bg-red-700 text-white">
                          {deletingPermanentlyLibrary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Permanently ({selectedProcedureKeys.size})
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                    {substantiveProcedures.map((proc, idx) => {
                      const procKey = `${proc.workspace}\0${proc.procedureName}`;
                      const isSelected = selectedProcedureKeys.has(procKey);
                      const isRestoring = restoringProcedure?.workspace === proc.workspace && restoringProcedure?.procedureName === proc.procedureName;
                      return (
                        <li
                          key={`${proc.workspace}-${proc.procedureName}-${idx}`}
                          className={`flex items-center gap-3 rounded-md border p-3 transition-colors cursor-pointer ${isSelected ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`}
                          onClick={() => { setSelectedProcedureKeys((prev) => { const n = new Set(prev); if (n.has(procKey)) n.delete(procKey); else n.add(procKey); return n; }); }}
                        >
                          <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); setSelectedProcedureKeys((prev) => { const n = new Set(prev); if (n.has(procKey)) n.delete(procKey); else n.add(procKey); return n; }); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-white">
                                {proc.procedureName}
                              </span>
                            </div>
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                Workspace: {proc.workspace}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleRestoreSubstantiveProcedure(proc.workspace, proc.procedureName); }} disabled={isRestoring} className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10" title="Restore">
                              {isRestoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  </>
                )}
              </>
            ) : selectedCategory === "romms" ? (
              <>
                {rommsLoading && <div className="text-sm text-white/60">Loading RoMMs...</div>}
                {!rommsLoading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!rommsLoading && !error && romms.length === 0 && (
                  <div className="text-sm text-white/60">No RoMMs in recycle bin.</div>
                )}
                {!rommsLoading && !error && romms.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={selectedRommIds.size === romms.length} onChange={() => { if (selectedRommIds.size === romms.length) setSelectedRommIds(new Set()); else setSelectedRommIds(new Set(romms.map((r) => r.id))); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500" />
                        <span className="text-sm text-white/60">{selectedRommIds.size > 0 ? `${selectedRommIds.size} selected` : "Select all"}</span>
                      </div>
                      {selectedRommIds.size > 0 && (
                        <Button size="sm" variant="destructive" onClick={handlePermanentDeleteRomms} disabled={deletingPermanentlyLibrary} className="bg-red-600 hover:bg-red-700 text-white">
                          {deletingPermanentlyLibrary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Permanently ({selectedRommIds.size})
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                    {romms.map((romm) => {
                      const isSelected = selectedRommIds.has(romm.id);
                      const isRestoring = restoringRomm === romm.id;
                      return (
                        <li
                          key={romm.id}
                          className={`flex items-center gap-3 rounded-md border p-3 transition-colors cursor-pointer ${isSelected ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`}
                          onClick={() => { setSelectedRommIds((prev) => { const n = new Set(prev); if (n.has(romm.id)) n.delete(romm.id); else n.add(romm.id); return n; }); }}
                        >
                          <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); setSelectedRommIds((prev) => { const n = new Set(prev); if (n.has(romm.id)) n.delete(romm.id); else n.add(romm.id); return n; }); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {romm.id}
                              </Badge>
                              {romm.workspace && (
                                <Badge variant="outline" className="text-xs">
                                  {romm.workspace}
                                </Badge>
                              )}
                              {romm.assertion && typeof romm.assertion === 'string' && (
                                <Badge variant="outline" className="text-xs">
                                  {getAssertionAbbreviation(romm.assertion)}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-white/80 mb-2">
                              {romm.risk}
                            </p>
                            {romm.controlIds && romm.controlIds.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-white/60 mb-1">Linked Controls:</p>
                                <div className="flex flex-wrap gap-1">
                                  {romm.controlIds.map((controlId: string, idx: number) => (
                                    <Badge key={idx} variant="outline" className="text-[10px]">
                                      {controlId}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleRestoreRomm(romm.id); }}
                              disabled={isRestoring}
                              className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                              title="Restore"
                            >
                              {isRestoring ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <RotateCcw className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  </>
                )}
              </>
            ) : selectedCategory === "controls" ? (
              <>
                {controlsLoading && <div className="text-sm text-white/60">Loading controls...</div>}
                {!controlsLoading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!controlsLoading && !error && controls.length === 0 && (
                  <div className="text-sm text-white/60">No controls in recycle bin.</div>
                )}
                {!controlsLoading && !error && controls.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={selectedControlIds.size === controls.length} onChange={() => { if (selectedControlIds.size === controls.length) setSelectedControlIds(new Set()); else setSelectedControlIds(new Set(controls.map((c) => c.controlId))); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500" />
                        <span className="text-sm text-white/60">{selectedControlIds.size > 0 ? `${selectedControlIds.size} selected` : "Select all"}</span>
                      </div>
                      {selectedControlIds.size > 0 && (
                        <Button size="sm" variant="destructive" onClick={handlePermanentDeleteControls} disabled={deletingPermanentlyLibrary} className="bg-red-600 hover:bg-red-700 text-white">
                          {deletingPermanentlyLibrary ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                          Delete Permanently ({selectedControlIds.size})
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                    {controls.map((control) => {
                      const isSelected = selectedControlIds.has(control.controlId);
                      const isRestoring = restoringControl === control.controlId;
                      return (
                        <li
                          key={control.controlId}
                          className={`flex items-center gap-3 rounded-md border p-3 transition-colors cursor-pointer ${isSelected ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"}`}
                          onClick={() => { setSelectedControlIds((prev) => { const n = new Set(prev); if (n.has(control.controlId)) n.delete(control.controlId); else n.add(control.controlId); return n; }); }}
                        >
                          <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); setSelectedControlIds((prev) => { const n = new Set(prev); if (n.has(control.controlId)) n.delete(control.controlId); else n.add(control.controlId); return n; }); }} className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {control.controlId}
                              </Badge>
                              <span className="text-sm font-medium text-white">
                                {control.controlName || control.controlId}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                {control.typeOfControl}
                              </Badge>
                              <Badge variant="outline" className="text-[10px]">
                                {control.nature}
                              </Badge>
                            </div>
                            {control.workspace && control.workspace.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-white/60 mb-1">Workspaces:</p>
                                <div className="flex flex-wrap gap-1">
                                  {control.workspace.map((ws: string, idx: number) => (
                                    <Badge key={idx} variant="outline" className="text-[10px]">
                                      {ws}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleRestoreControl(control.controlId); }}
                              disabled={isRestoring}
                              className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                              title="Restore"
                            >
                              {isRestoring ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <RotateCcw className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  </>
                )}
              </>
            ) : (
              <>
                {loading && <div className="text-sm text-white/60">Loading...</div>}
                {!loading && error && (
                  <div className="text-sm text-red-400">{error}</div>
                )}
                {!loading && !error && filteredDocs.length === 0 && (
                  <div className="text-sm text-white/60">No documents in recycle bin.</div>
                )}
                {!loading && !error && filteredDocs.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedFiles.size === filteredDocs.length && filteredDocs.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="text-sm text-white/60">
                          {selectedFiles.size > 0
                            ? `${selectedFiles.size} file(s) selected`
                            : "Select all"}
                        </span>
                      </div>
                      {selectedFiles.size > 0 && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handleDeletePermanently}
                          disabled={deletingPermanently}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          {deletingPermanently ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Permanently ({selectedFiles.size})
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">
                      {filteredDocs.map((d, idx) => {
                        const isSelected = selectedFiles.has(d.name);
                        return (
                          <li
                            key={`${d.name}-${idx}`}
                            className={`flex items-center gap-3 rounded-md border p-3 transition-colors ${
                              isSelected
                                ? "border-blue-500/50 bg-blue-500/10"
                                : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleFileSelection(d.name)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 flex-shrink-0"
                            />
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => {
                                e.preventDefault();
                                handleFileClick(d.url);
                              }}
                              className="text-sm text-blue-300 hover:underline cursor-pointer flex-1"
                            >
                              {d.name}
                            </a>
                            <div className="flex items-center gap-2 ml-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRestore(d.name)}
                                className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                title="Restore"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

